import { computed } from '@vue/composition-api';
import useAuth from "@/useAuth";
import publicNavMenuItems from "@/navigation/horizontal";
import adminNavMenuItems from "@/navigation/vertical";
import { i18n } from "@/plugins/i18n";
import usePageCategories from "@/@core/utils/usePageCategories";
import { mdiInformationOutline } from '@mdi/js';
// import useAdminNotifications from "@/@core/utils/useAdminNotifications";


export default function useMenus() {

    const { tenant, user, userIsAdmin } = useAuth();
    const { pageCategories, fetchPageCategories, isLoaded: pageCategoriesLoaded } = usePageCategories();
    // const { newFeedbackCount } = useAdminNotifications();


    const getTenantTerm = (termKey, tenantTermTranslations, lang) => {

        if (!tenantTermTranslations || !termKey)
            return null;

        if (!tenantTermTranslations[lang])
            return null;

        return tenantTermTranslations[lang][termKey] ?? null;
    }


    const getMenus = (defaultMenuItems) => {
        defaultMenuItems.forEach((menu) => {
            if (menu.dynamicTitle) {
                const tenantTerm = getTenantTerm(
                    menu.dynamicTitleKey,
                    tenant.value.terms?.translations,
                    i18n.locale
                );
                if (tenantTerm) {
                    menu.title = tenantTerm;
                }
            }

            if (menu.children && menu.children.length) {
                menu.children.forEach((child) => {
                    if (child.dynamicTitle) {
                        const tenantChildTerm = getTenantTerm(
                            child.dynamicTitleKey,
                            tenant.value.terms?.translations,
                            i18n.locale
                        );

                        if (tenantChildTerm) {
                            child.title = tenantChildTerm;
                        }
                    }
                });
            }
        });

        return defaultMenuItems.filter((m) => {
            if (user.value.read_only && m.disabled_read_only === true) {
                return false;
            }

            if (m.optional) {
                return tenant.value.settings != null ? tenant.value.settings[m.optional_key] : true;
            }

            if (m.children && m.children.length) {
                const children = m.children.filter((c) => {

                    if (user.value.read_only && c.disabled_read_only === true) {
                        return false;
                    }

                    if (c.optional) {
                        return tenant.value.settings != null ? tenant.value.settings[c.optional_key] : true;
                    }

                    return true;
                });

                m.children = children;
            }

            return true;
        });
    }

    const getTenantPublicMenuItems = () => {
        const MAX_ROOT_ITEMS = 6;

        // Deep clone to prevent mutations of the original menu array
        let tenantMenus = JSON.parse(JSON.stringify(publicNavMenuItems));
        tenantMenus = getMenus(tenantMenus);

        // Find and replace the "company-pages" placeholder with dynamic page categories
        const companyPagesIndex = tenantMenus.findIndex(m => m.to === 'company-pages');
        if (companyPagesIndex !== -1) {
            // Remove the placeholder item
            tenantMenus.splice(companyPagesIndex, 1);

            // Insert dynamic page category items at that position
            const categoryMenuItems = generatePublicPageCategoryMenuItems();
            tenantMenus.splice(companyPagesIndex, 0, ...categoryMenuItems);
        }

        // Handle overflow: move excess items to submenus
        const submenusIndex = tenantMenus.findIndex(m => m.key === 'submenus');

        if (submenusIndex > MAX_ROOT_ITEMS) {
            // Get items between position MAX_ROOT_ITEMS and submenus
            const excessItems = tenantMenus.splice(MAX_ROOT_ITEMS, submenusIndex - MAX_ROOT_ITEMS);

            // Add to beginning of submenus children
            const submenus = tenantMenus[MAX_ROOT_ITEMS]; // Now at position MAX_ROOT_ITEMS
            submenus.children = [...excessItems, ...submenus.children];
        }

        tenantMenus.forEach(menu => {
            if (menu.children && menu.children.length) {
                if (!user.value.is_tenant_admin && !userIsAdmin.value) {
                    menu.children = menu.children.filter(c => !(!!c.admin));
                }
            }
        });

        return tenantMenus;
    }

    const generatePublicPageCategoryMenuItems = () => {
        if (!tenant.value.page_categories || tenant.value.page_categories.length === 0) {
            return [];
        }
        return tenant.value.page_categories.map(category => {
            return {
                title: category.title,
                icon: mdiInformationOutline,
                to: 'company-pages',
                queryParams: { category: category.id },
                activeQuery: { category: category.id },
                isPageCategory: true,
                categoryId: category.id,
                isDefault: category.is_default
            };
        });
    }

    /**
     * Generate dynamic menu items for page categories
     */
    const generateAdminPageCategoryMenuItems = () => {
        if (!pageCategories.value || pageCategories.value.length === 0) {
            return [];
        }

        return pageCategories.value.map(category => ({
            title: category.title,
            icon: mdiInformationOutline,
            to: { name: 'admin-page-list', query: { category: category.id } },
            activeQuery: { category: category.id },
            tenantAdmin: true,
            isPageCategory: true,
            categoryId: category.id,
            isDefault: category.is_default,
            isActive: category.is_active
        }));
    }

    const getTenantAdminMenuItems = () => {
        let tenantMenus = getMenus(adminNavMenuItems);

        // Find the index of the placeholder page-list item and replace with dynamic categories
        const pageListIndex = tenantMenus.findIndex(m => m.to === 'admin-page-list');
        if (pageListIndex !== -1) {
            // Remove the placeholder item
            tenantMenus.splice(pageListIndex, 1);

            // Insert dynamic page category items at that position
            const categoryMenuItems = generateAdminPageCategoryMenuItems();
            tenantMenus.splice(pageListIndex, 0, ...categoryMenuItems);
        }

        if (!user.value.is_feedback_manager) {
            tenantMenus = tenantMenus.filter(m => !m.feedbackManager);
        }
        if (user.value.is_tenant_admin) {
            return tenantMenus;
        }

        return tenantMenus.filter(m => !m.tenantAdmin);
    }

    // Make publicMenusItems reactive with computed
    const publicMenusItems = computed(() => getTenantPublicMenuItems());

    return {
        getTenantTerm,
        publicMenusItems,
        getTenantAdminMenuItems,
        fetchPageCategories,
        pageCategories,
        pageCategoriesLoaded
    };
}
